import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Flex, message, Modal, Spin, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { DayPilotScheduler } from '@daypilot/daypilot-lite-react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import DraggablePopup from '../../components/DraggablePopup/DraggablePopup';
// Session related selectors
import { selectIsLoading, selectUserSettings } from '../../store/reducers/sessionReducer';
import { ServiceCalendarPopupAddEvent } from '../../components/calendar/popups/ServiceCalendarPopupAddEvent';
import { ServiceCalendarPopupFilter } from '../../components/calendar/popups/ServiceCalendarPopupFilter';
import { ServiceCalendarPopupShowColors } from '../../components/calendar/popups/ServiceCalendarPopupShowColors';
import { ServiceCalendarPopupShowEvent } from '../../components/calendar/popups/ServiceCalendarPopupShowEvent';
import { ServiceCalendarPopupShowVehicle } from '../../components/calendar/popups/ServiceCalendarPopupShowVehicle';
import { ServiceCalendarPopupYearView } from '../../components/calendar/popups/ServiceCalendarPopupYearView';
// Calendar components
import TopSection from '../../components/calendar/TopSection';
import '../../css/serviceCalendar.css';
// Redux actions and selectors for calendar functionality
import {
    clearService,
    fetchInitialCalendarData,
    fetchServices,
    initializeCalendarFilters,
    moveEvent,
    resetFilters,
    selectCalendarDateCompareOperator,
    selectCalendarError,
    selectCalendarEvents,
    selectCalendarIntervalMonths,
    selectCalendarLoadingStatus,
    selectFilteredCalendarResources,
    selectCalendarViewStartDate,
    selectEventMovingStatus,
    selectIsFilterPopupVisible,
    selectIsNewEventPopupVisible,
    selectIsServicePopupVisible,
    selectIsStatusPopupVisible,
    selectIsVehiclePopupVisible,
    selectIsYearViewPopupVisible,
    setEventPopupDetails,
    setNewEventPopupDetails,
    setPopupVisibility,
    setVehiclePopupDetails,
    updateCalendarResources
} from '../../store/serviceCalendarSlice';
import { ServiceCalendarTitle } from '../../components/calendar/ServiceCalendarTitle';
import { calculatePopupPosition, getNumberDays } from '../../utils/ServiceCalendarUtils';
import { renderMessageWithBold } from '../../utils/renderMessageBold';
import { QuestionCircleFilled, ReloadOutlined } from '@ant-design/icons';

const ServiceCalendar = () => {
    const dispatch = useDispatch();
    const selfRef = useRef(null);
    const scheduler = useRef(null);

    const resources = useSelector(selectFilteredCalendarResources);
    const events = useSelector(selectCalendarEvents);
    const viewStartDate = useSelector(selectCalendarViewStartDate);
    const intervalMonths = useSelector(selectCalendarIntervalMonths);
    const selectedDateCompareOperator = useSelector(selectCalendarDateCompareOperator);

    // Select popup visibility states
    const isStatusPopupVisible = useSelector(selectIsStatusPopupVisible);
    const isServicePopupVisible = useSelector(selectIsServicePopupVisible);
    const isFilterPopupVisible = useSelector(selectIsFilterPopupVisible);
    const isYearViewPopupVisible = useSelector(selectIsYearViewPopupVisible);
    const isVehiclePopupVisible = useSelector(selectIsVehiclePopupVisible);
    const isNewEventPopupVisible = useSelector(selectIsNewEventPopupVisible);

    // Select loading and error states
    const loadingStatus = useSelector(selectCalendarLoadingStatus);
    const error = useSelector(selectCalendarError);
    const userSettings = useSelector(selectUserSettings);
    const isUserSettingsLoading = useSelector(selectIsLoading);
    const eventMovingStatus = useSelector(selectEventMovingStatus);

    // Local state for event moving and popups
    const [movingEventId, setMovingEventId] = useState(null);
    const [showEventPopupDisplayStyle, setShowEventPopupDisplayStyle] = useState('none');
    const [highlightedEventId, setHighlightedEventId] = useState(null);
    const [highlightedEventType, setHighlightedEventType] = useState(null);

    const [previewLoading, setPreviewLoading] = useState(false);
    const [moveButtonLoading, setMoveButtonLoading] = useState('');
    const [isSingleMoveDisabled, setIsSingleMoveDisabled] = useState(false);
    // state to force scheduler re-render
    const [schedulerKey, setSchedulerKey] = useState(0);

    // State for confirmation modal when moving events
    const [confirmationModal, setConfirmationModal] = useState({
        visible: false,
        new_date: null,
        type: null, // 'statutory' or 'autoScheduled'
        message: '',
        eventArgs: null, // Original event move arguments
        deltaDays: null, // Number of days to move
        originalData: null, // Original event data
        needsStatutoryConfirmation: false, // Flag for additional confirmation
        needsAutoScheduleConfirmation: false, // Flag for additional confirmation
        relatedServices: [] // Array of services affected by the move
    });

    // Refs for popup containers
    const newEventPopupRef = useRef(null);
    const showVehiclePopupRef = useRef(null);
    const showEventPopupRef = useRef(null);

    // Inside the ServiceCalendar component, add:
    const location = useLocation();
    const navigate = useNavigate();

    // Add this effect to handle highlighting
    useEffect(() => {
        if (location.state?.highlightedDate && location.state?.id && events.length > 0) {
            setHighlightedEventId(location.state.id);
            setHighlightedEventType(location.state?.recordType);
            const event = events.find((ev) => ev.serviceId === location.state.id && ev.recordType === location.state.recordType);
            scheduler.current.events.focus(scheduler.current.events.find(event?.id)); // Focus on the highlighted event
            //set the location state to null to avoid re-highlighting on re-renders
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [events, location.state]);

    useEffect(() => {
        if (highlightedEventId) {
            // Set a timeout to reset the highlight after 5 seconds
            const timeout = setTimeout(() => {
                setHighlightedEventId(null);
                setHighlightedEventType(null);

                // Remove the CSS class from the highlighted event
                const highlightedElement = document.querySelector('.highlighted-event');
                if (highlightedElement) {
                    highlightedElement.classList.remove('highlighted-event');
                }
            }, 5000);

            // Cleanup the timeout if the component unmounts or highlightedEventId changes
            return () => clearTimeout(timeout);
        }
    }, [highlightedEventId]);

    useEffect(() => {
        if (confirmationModal?.data?.autoScheduleResults && confirmationModal.data.autoScheduleResults.length > 0 && confirmationModal?.data?.id) {
            // Find the result by a specific id
            const result = confirmationModal.data.autoScheduleResults.find((item) => item.id === confirmationModal.data.id);

            if (!result?.success) {
                setIsSingleMoveDisabled(true);
            } else {
                setIsSingleMoveDisabled(false);
            }
        } else {
            setIsSingleMoveDisabled(false);
        }
    }, [confirmationModal]);

    /**
     * Closes all open popups in the calendar
     */
    const closeAllPopups = useCallback(() => {
        // Close all Redux-managed popups
        dispatch(setPopupVisibility({ popupName: 'service', isVisible: false }));
        dispatch(setPopupVisibility({ popupName: 'status', isVisible: false }));
        dispatch(setPopupVisibility({ popupName: 'filter', isVisible: false }));
        dispatch(setPopupVisibility({ popupName: 'newEvent', isVisible: false }));
        dispatch(setPopupVisibility({ popupName: 'yearView', isVisible: false }));
        dispatch(setPopupVisibility({ popupName: 'vehicle', isVisible: false }));

        // Close locally-managed popup
        setShowEventPopupDisplayStyle('none');
    }, [dispatch]);

    // Load user settings and initialize filters when settings are available
    useEffect(() => {
        if (isUserSettingsLoading) return;

        const calendarFilters = userSettings?.service_calendar_filters || {};
        dispatch(initializeCalendarFilters(calendarFilters));

        // Apply vehicle filter to calendar resources if saved in user settings
        if (calendarFilters.selectedVehicles && calendarFilters.selectedVehicles.length > 0) {
            dispatch(updateCalendarResources({ selectedVehicleIds: calendarFilters.selectedVehicles }));
        }

        dispatch(fetchServices());
        dispatch(fetchInitialCalendarData());
    }, [dispatch, userSettings, isUserSettingsLoading]);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            closeAllPopups();
            dispatch(resetFilters({}));
            dispatch(clearService());
        };
    }, [dispatch, closeAllPopups]);

    /**
     * Handles time range selection in the calendar (creating new events)
     */
    const handleTimeRangeSelected = useCallback(
        (args) => {
            const dp = args.control;
            const currentCell = dp.getCoords();
            const rect = selfRef.current.getBoundingClientRect();
            const timeHeaderHeight = 60; // Height of time header in pixels
            const popupHeight = 273; // Height of the new event popup

            // Calculate popup position based on clicked cell
            const position = calculatePopupPosition(
                currentCell.x + rect.x + dp.rowHeaderWidth,
                currentCell.y + rect.y + timeHeaderHeight,
                popupHeight
            );

            const eventStartDate = dayjs(args.start.value).format('YYYY-MM-DD');
            dispatch(
                setNewEventPopupDetails({
                    vehicle: { vehicleId: args.resource, date: eventStartDate },
                    position
                })
            );
            dispatch(setPopupVisibility({ popupName: 'newEvent', isVisible: true }));

            // Close other open popups
            dispatch(setPopupVisibility({ popupName: 'filter', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'yearView', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'vehicle', isVisible: false }));
            setShowEventPopupDisplayStyle('none');
        },
        [dispatch]
    );

    /**
     * Handles clicking on calendar events
     */
    const handleEventClick = useCallback(
        (args) => {
            args.preventDefault();
            const eventData = args.e.data;

            // Calculate popup position based on click coordinates
            const clickX = args.originalEvent.pageX;
            const clickY = args.originalEvent.pageY;
            const position = calculatePopupPosition(clickX, clickY);

            // Format event date consistently
            const eventStartDate =
                eventData.start && typeof eventData.start !== 'object' ? eventData.start : dayjs(eventData.start.value).format('YYYY-MM-DD');

            dispatch(
                setEventPopupDetails({
                    event: { ...eventData, date: eventStartDate, start: eventStartDate, end: eventStartDate },
                    position
                })
            );

            // Show the event popup and close others
            setShowEventPopupDisplayStyle('block');
            dispatch(setPopupVisibility({ popupName: 'filter', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'yearView', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'vehicle', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'newEvent', isVisible: false }));
        },
        [dispatch]
    );

    /**
     * Handles clicking on vehicle rows in the calendar
     */
    const handleRowClick = useCallback(
        (args) => {
            const vehicleData = args.row.data;
            const serviceDueDate = vehicleData.serviceDue;
            const clickX = args.originalEvent.pageX;
            const clickY = args.originalEvent.pageY;

            // Calculate popup position ensuring it stays within viewport
            const POPUP_HEIGHT = 370;
            const VIEWPORT_MARGIN = 10;
            const viewportHeight = window.innerHeight;

            let finalTop = clickY;
            if (clickY + POPUP_HEIGHT > viewportHeight - VIEWPORT_MARGIN) {
                finalTop = viewportHeight - POPUP_HEIGHT - VIEWPORT_MARGIN;
            }
            finalTop = Math.max(VIEWPORT_MARGIN, finalTop);

            dispatch(
                setVehiclePopupDetails({
                    vehicle: { ...vehicleData, serviceDue: serviceDueDate },
                    position: { left: clickX, top: finalTop }
                })
            );

            // Show vehicle popup and close others
            dispatch(setPopupVisibility({ popupName: 'vehicle', isVisible: true }));
            dispatch(setPopupVisibility({ popupName: 'filter', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'yearView', isVisible: false }));
            dispatch(setPopupVisibility({ popupName: 'newEvent', isVisible: false }));
            setShowEventPopupDisplayStyle('none');
        },
        [dispatch]
    );

    /**
     * Dispatches the move event action after confirmation
     */
    const dispatchMoveAction = useCallback(
        (payload, type, moveType, originalData) => {
            setMovingEventId({ id: payload.id, type: payload.type });
            if (moveType) setMoveButtonLoading(moveType);

            dispatch(moveEvent({ eventData: payload, serviceType: type, moveType: moveType }))
                .unwrap()
                .then((response) => {
                    // console.log(response);
                    if (!response?.success) {
                        // Revert visual change on error
                        if (scheduler.current && originalData) {
                            scheduler.current.events.update({
                                ...originalData
                            });
                        }
                        message.error(renderMessageWithBold(response.message) || 'Error moving service');
                    } else {
                        message.success(renderMessageWithBold(response.message) || 'Service moved successfully');
                    }

                    if (type === 'autoScheduled' && response?.success) dispatch(fetchServices());
                })
                .catch((error) => {
                    // Revert visual change on error
                    if (scheduler.current && originalData) {
                        scheduler.current.events.update({
                            ...originalData
                        });
                    }
                    message.error(renderMessageWithBold(error?.message) || 'Error moving service');
                })
                .finally(() => {
                    setMovingEventId(null);
                    setMoveButtonLoading(null);
                    setConfirmationModal({
                        visible: false
                    });
                });
        },
        [dispatch, scheduler, confirmationModal.type, confirmationModal.new_date, confirmationModal.originalData]
    );

    const dispatchPreviewAction = useCallback(
        (payload, originalData) => {
            setPreviewLoading(true);
            setConfirmationModal({ visible: true });

            dispatch(moveEvent({ eventData: payload, serviceType: 'autoScheduled', moveType: null }))
                .unwrap()
                .then((response) => {
                    if (response?.status === 409 || response?.status === 200)
                        setConfirmationModal((prev) => ({ ...prev, data: response, originalData: originalData, payload: payload, visible: true }));
                })
                .catch((error) => {
                    if (error?.status !== 409 || error?.status !== 200) {
                        setConfirmationModal({ visible: false });
                    }
                    const apiMsg = renderMessageWithBold(error?.message);
                    message.error(apiMsg || 'Error updating service');
                })
                .finally(() => {
                    setPreviewLoading(false);
                });
        },
        [dispatch, scheduler, confirmationModal.type, confirmationModal.new_date, confirmationModal.originalData]
    );

    /**
     * Handles moving events in the calendar with validation and confirmation
     */
    const handleEventMove = useCallback(
        (args) => {
            closeAllPopups();
            const originalData = { ...args.e.data };

            // Calculate days moved and validate against current date
            const eventStart = dayjs(args.newStart.value);
            const originalStart = dayjs(originalData.start);
            const delta_days = Math.round(eventStart.diff(originalStart, 'day'));

            let payload = {
                id: originalData.serviceId,
                days: delta_days,
                type: originalData.recordType === 'Service' ? 'service' : 'defect'
            };

            const resource = args.e.resource();
            const newResource = args.newResource;
            if (resource !== newResource) {
                payload = { ...payload, move_to_vehicle_id: newResource };
            }

            const isAutoScheduled = originalData.autoScheduled;

            if (isAutoScheduled) {
                dispatchPreviewAction(payload, originalData);
                return;
            }
            dispatchMoveAction(payload, null, null, originalData);
        },
        [closeAllPopups, dispatchMoveAction]
    );

    /**
     * Cancels event move and reverts visual changes
     */
    const handleCancelConfirmation = useCallback(() => {
        const { originalData } = confirmationModal;

        if (scheduler.current && originalData) {
            scheduler.current.events.update({
                ...originalData
            });
            message.info('Move cancelled.');
        }
        setConfirmationModal({
            visible: false,
            data: []
        });
    }, [confirmationModal]);

    const handleFix = (conflictInfo) => {
        // Logic to handle fixing the conflict
        if (conflictInfo?.type === 'service') {
            //redirect to service edit page
            window.open(`/scheduled-services/add?id=${conflictInfo?.id}`, '_blank');
        } else if (conflictInfo?.type === 'unscheduled') {
            //redirect to unscheduled service edit page
            window.open(`/unscheduled-services/add?id=${conflictInfo?.id}`, '_blank');
        } else if (conflictInfo?.type === 'defect') {
            //redirect to defect edit page
            window.open(`/defect-tickets/add?id=${conflictInfo?.id}`, '_blank');
        }
    };

    /**
     * Handles clicks outside popups to close them
     */
    const handleClickOutside = useCallback(
        (event) => {
            if (
                showVehiclePopupRef.current &&
                !showVehiclePopupRef.current.contains(event.target) &&
                !event.target.closest('.scheduler_default_rowheader_scroll')
            ) {
                dispatch(setPopupVisibility({ popupName: 'vehicle', isVisible: false }));
            }
            if (
                newEventPopupRef.current &&
                !newEventPopupRef.current.contains(event.target) &&
                !event.target.closest('.scheduler_default_rowheader_scroll')
            ) {
                dispatch(setPopupVisibility({ popupName: 'newEvent', isVisible: false }));
            }
            if (
                showEventPopupRef.current &&
                !showEventPopupRef.current.contains(event.target) &&
                !event.target.closest('.scheduler_default_rowheader_scroll')
            ) {
                setShowEventPopupDisplayStyle('none');
            }
        },
        [dispatch]
    );

    // Add/remove click outside listener
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    // Hide event popup
    const hideEventPopup = useCallback(() => {
        setShowEventPopupDisplayStyle('none');
    }, []);

    // Add effect to handle year view modal state changes
    useEffect(() => {
        // When year view modal closes, force complete re-initialization
        if (!isYearViewPopupVisible && scheduler.current) {
            const timeoutId = setTimeout(() => {
                try {
                    // Force scheduler to completely re-render by changing its key
                    setSchedulerKey((prev) => prev + 1);
                } catch (error) {
                    console.warn('Error refreshing calendar:', error);
                }
            }, 200);

            return () => clearTimeout(timeoutId);
        }
    }, [isYearViewPopupVisible, dispatch]);

    // DayPilot scheduler configuration
    const config = useMemo(
        () => ({
            autoResize: true, // Enable auto-resizing
            cellWidthSpec: 'Fixed',
            cellWidth: 24,
            eventHeight: 24,
            rowHeaderWidth: 155,
            durationBarVisible: false,
            timeHeaders: [{ groupBy: 'Month' }, { groupBy: 'Day', format: 'd' }],
            scale: 'Day',
            cornerHtml: '<span>Vehicle</span>',
            //cornerText: 'Vehicle',
            timeRangeSelectedHandling: 'Enabled',
            eventClickHandling: 'Enabled',
            eventMoveHandling: 'Enabled',
            rowClickHandling: 'Enabled',
            eventMarginTop: 2,
            eventMarginBottom: 1,
            timeRangeSelectingStartEndEnabled: false,
            eventMovingStartEndEnabled: true,
            eventResizeHandling: 'Disabled',
            treeEnabled: false,
            messageHideAfter: 0,
            startDate: viewStartDate ? dayjs(viewStartDate).toDate() : new Date(),

            // Event handlers
            onTimeRangeSelected: handleTimeRangeSelected,
            onEventClick: handleEventClick,
            onRowClick: handleRowClick,

            // Custom cell rendering
            onBeforeCellRender: (args) => {
                if (args.cell.properties.business === false) {
                    args.cell.backColor = '#E1EEF5';
                } else {
                    // Highlight today's date
                    const pad = (num) => num.toString().padStart(2, '0');
                    const date = new Date();
                    const yyyy = date.getFullYear();
                    const mm = pad(date.getMonth() + 1);
                    const dd = pad(date.getDate());
                    let todayDateStr = `${yyyy}-${mm}-${dd}`;

                    if (args.cell.start.toString('yyyy-MM-dd') === todayDateStr) {
                        args.cell.backColor = '#FFF8E6';
                    }
                }
            },

            // Custom event rendering
            onBeforeEventRender: (args) => {
                args.data.borderColor = args.data?.borderColor || '#000';
                // console.log(args.data);
                // Show loading spinner for moving events
                if (
                    args.data.serviceId === movingEventId?.id &&
                    args.data.recordType?.toLowerCase() === movingEventId?.type?.toLowerCase() &&
                    eventMovingStatus === 'loading'
                ) {
                    args.data.html = `
                    <div style="width:100%; text-align:center; color:white; position:relative;">
                        <div style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);">
                            <div class="event-loading-spinner"></div>
                        </div>
                    </div>
                `;
                }
                args.data.html = `<div class="text-event-box" title="${args.data.serviceType} (${args.data.status})">
                                               ${String(args.data.serviceType || '?').charAt(0)}
                                       </div>`;

                // if (args.data.serviceType === 'Multiple') {
                //     // Special rendering for multiple service events
                //     args.data.html = `
                //     <div style="width:100%; text-align:center; color:white" title="${args.data.serviceType} (${args.data.status})">
                //         M
                //     </div>
                // `;
                // }
                if (args.data.serviceId === highlightedEventId && args.data.recordType === highlightedEventType) {
                    args.data.cssClass = 'highlighted-event';
                }
            },
            onBeforeRowHeaderRender: (args) => {
                //args.html = '<span>Vehicle</span>';
                //console.log("onBeforeRowHeaderRender", args);
            },

            onEventMove: handleEventMove
        }),
        [
            viewStartDate,
            handleTimeRangeSelected,
            handleEventClick,
            handleRowClick,
            handleEventMove,
            movingEventId,
            eventMovingStatus,
            highlightedEventId,
            highlightedEventType
        ]
    );

    // Calculate number of days to display based on view settings
    const days = useMemo(() => {
        if (!viewStartDate) return 30;
        const startDateForCalc = dayjs(viewStartDate).toDate();
        const numDays = getNumberDays(startDateForCalc, intervalMonths);
        return selectedDateCompareOperator === 'smaller' ? numDays + 1 : numDays;
    }, [viewStartDate, intervalMonths, selectedDateCompareOperator]);

    return (
        <div className="div-service-calendar">
            <ServiceCalendarTitle onRefreshDate={() => dispatch(fetchServices())} titleHeadingText="Services Calendar" />
            <TopSection page="calendar" />

            {/* Loading and error states */}
            {loadingStatus === 'loading' && <div>Loading Calendar...</div>}

            {/* Main calendar view */}
            <div ref={selfRef}>
                <Spin spinning={loadingStatus === 'loading'}>
                    <DayPilotScheduler key={schedulerKey} {...config} resources={resources} events={events} days={days} controlRef={scheduler} />
                </Spin>
            </div>

            {/* Popup components */}
            {isServicePopupVisible && (
                <DraggablePopup title="Service Types" offsetIndex={0}>
                    <ServiceCalendarPopupShowColors type="service" />
                </DraggablePopup>
            )}
            {isStatusPopupVisible && (
                <DraggablePopup title="Status Types" offsetIndex={1}>
                    <ServiceCalendarPopupShowColors type="status" />
                </DraggablePopup>
            )}
            {isFilterPopupVisible && (
                <div>
                    <ServiceCalendarPopupFilter type="calendar" />
                </div>
            )}
            {isYearViewPopupVisible && (
                <div>
                    <ServiceCalendarPopupYearView />
                </div>
            )}

            {/* Event move confirmation modal */}
            <Modal
                title="Confirm Move"
                open={confirmationModal.visible}
                onCancel={handleCancelConfirmation}
                loading={previewLoading}
                footer={[
                    <Button key="back" onClick={handleCancelConfirmation} disabled={eventMovingStatus === 'loading'}>
                        Cancel
                    </Button>,
                    <Button
                        key="moveOne"
                        onClick={() =>
                            dispatchMoveAction(
                                {
                                    days: confirmationModal?.data?.days,
                                    id: confirmationModal?.data?.id,
                                    type: confirmationModal?.data?.type,
                                    ...(confirmationModal?.data?.move_to_vehicle_id
                                        ? { move_to_vehicle_id: confirmationModal.data.move_to_vehicle_id }
                                        : {})
                                },
                                'autoScheduled',
                                'single',
                                confirmationModal?.originalData
                            )
                        }
                        disabled={isSingleMoveDisabled || (eventMovingStatus === 'loading' && moveButtonLoading === 'all')}
                        loading={eventMovingStatus === 'loading' && moveButtonLoading === 'single'}>
                        Move Only This Service
                    </Button>,
                    <Button
                        key="moveAll"
                        type="primary"
                        onClick={() =>
                            dispatchMoveAction(
                                {
                                    days: confirmationModal?.data?.days,
                                    id: confirmationModal?.data?.id,
                                    type: confirmationModal?.data?.type,
                                    ...(confirmationModal?.data?.move_to_vehicle_id
                                        ? { move_to_vehicle_id: confirmationModal.data.move_to_vehicle_id }
                                        : {})
                                },
                                'autoScheduled',
                                'all',
                                confirmationModal?.originalData
                            )
                        }
                        disabled={!confirmationModal?.data?.success || (eventMovingStatus === 'loading' && moveButtonLoading === 'single')}
                        loading={eventMovingStatus === 'loading' && moveButtonLoading === 'all'}>
                        Move All Related Services
                    </Button>
                ]}
                centered
                width={500}
                rootClassName="custom-modal">
                <div style={{ color: '#3C8DBC' }} className="font-bold">
                    {confirmationModal?.data?.autoScheduleResults ? confirmationModal?.data.autoScheduleResults[0]?.service_type : 'N/A'}
                </div>

                {!confirmationModal?.data?.success && (
                    <div className="text-red-500 mt-2 text-left text-md">{renderMessageWithBold(confirmationModal?.data?.message)}</div>
                )}
                <div className="my-2">
                    Total services scheduled: <strong style={{ color: '#3C8DBC' }}>{confirmationModal?.data?.autoScheduleResults?.length}</strong>
                </div>

                <div
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4
                    }}>
                    <table
                        style={{
                            width: '100%',
                            borderCollapse: 'collapse'
                        }}>
                        <thead>
                            <tr
                                style={{
                                    backgroundColor: '#fafafa',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1
                                }}>
                                <th
                                    style={{
                                        padding: '12px 16px',
                                        textAlign: 'left',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}>
                                    #
                                </th>
                                <th style={{ borderBottom: '1px solid #e8e8e8', padding: '8px', textAlign: 'left' }}> Date</th>
                                <th
                                    style={{
                                        padding: '12px 16px',
                                        textAlign: 'left',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}>
                                    Conflict
                                </th>
                                <th>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => dispatchPreviewAction(confirmationModal?.payload, confirmationModal?.originalData)}
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {confirmationModal?.data?.autoScheduleResults?.map((result, index) => (
                                <tr
                                    key={index}
                                    style={{
                                        borderBottom: '1px solid #f0f0f0',
                                        '&:hover': {
                                            backgroundColor: '#fafafa'
                                        }
                                    }}>
                                    <td style={{ padding: '12px 16px' }}>{index + 1}</td>
                                    <td style={{ borderBottom: '1px solid #e8e8e8', padding: '8px' }}>
                                        {dayjs(result.start_date).format('DD MMM YYYY')}
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        {result?.conflictInfo ? (
                                            <Flex>
                                                <Tag color="red">Unavailable</Tag>
                                                <Tooltip
                                                    placement="topLeft"
                                                    title={renderMessageWithBold(result?.conflictInfo?.detail) || 'Conflict detected'}>
                                                    <QuestionCircleFilled style={{ fontSize: '30px', color: '#2477A8' }} className="info-icon" />
                                                </Tooltip>
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    onClick={() => handleFix(result.conflictInfo)}
                                                    disabled={!result.conflictInfo}
                                                    className="ml-2">
                                                    Fix
                                                </Button>
                                            </Flex>
                                        ) : (
                                            <Tag color="green">Available</Tag>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal>

            {/* Vehicle and event popups */}
            <div ref={showVehiclePopupRef}>{isVehiclePopupVisible && <ServiceCalendarPopupShowVehicle />}</div>
            <div ref={newEventPopupRef}>{isNewEventPopupVisible && <ServiceCalendarPopupAddEvent />}</div>
            <div ref={showEventPopupRef}>
                <ServiceCalendarPopupShowEvent displayStyle={showEventPopupDisplayStyle} handleClose={hideEventPopup} />
                <div
                    onClick={hideEventPopup}
                    style={{
                        display: showEventPopupDisplayStyle,
                        zIndex: 9
                    }}></div>
            </div>
        </div>
    );
};
export default ServiceCalendar;
